import { IPlaybackOutlet, Playback } from 'app/services/rtc/Playback';
import React, { Component, ComponentClass } from 'react';
import { createStyles } from '../../utils/css';
import MediaElement, { MediaType } from './MediaElement';
import { NonCriticalError } from 'app/constants/NonCriticalError';
import { notify } from 'app/services/errorReporter';

export interface ISimpleSoundPlayerProps {
  src: string;
}

export interface ISimpleSoundPlayer extends Component<ISimpleSoundPlayerProps> {
  play(): Promise<void>;
}

export interface ISimpleSoundPlayerType extends ComponentClass<ISimpleSoundPlayerProps> {
  new (props: ISimpleSoundPlayerProps, context?: any): ISimpleSoundPlayer;
}

/**
 * Simple media player with blocking playback. No visible elements.
 */
export class SimpleSoundPlayer
  extends Component<ISimpleSoundPlayerProps>
  implements ISimpleSoundPlayer {
  private outlet: IPlaybackOutlet | null = null;

  private readonly loadedPromise: Promise<void>;

  // Callback for the result of loading. Used to determine if this control is loading.
  private onLoaded: ((err?: any) => void) | null = null;

  // Callback for the result of playing. Used to determine if this contorl is playing.
  private onPlaybackFinished: (() => void) | null = null;

  private readonly playback: Playback = {
    addPlaybackOutlet: (x): void => {
      this.outlet = x;
    },
    removePlaybackOutlet: (x) => {
      this.outlet = null;
      if (this.onLoaded) {
        this.onLoaded(new NonCriticalError('removePlaybackOutlet interrupted load'));
      }
      if (this.onPlaybackFinished) {
        // TODO: which events are triggered on unmount, if any?
        this.onPlaybackFinished();
      }
    },
    onCanPlay: () => {
      if (this.onLoaded) {
        this.onLoaded();
      }
    },
    onEnded: () => {
      if (this.onPlaybackFinished) {
        this.onPlaybackFinished();
      }
    },
    onSoundBlocked: () => {
      if (this.onPlaybackFinished) {
        this.onPlaybackFinished();
      }
    },
    onError: (e: MediaError) => {
      if (this.onLoaded) {
        // An error during loading is probably terminal.
        // An error after loading is probably transient.
        this.onLoaded(e);
      }
      // We assume errors do not change the playback state.
    },
  };

  public constructor(initialProps: ISimpleSoundPlayerProps) {
    super(initialProps);

    this.loadedPromise = new Promise((resolve, reject) => {
      this.onLoaded = (err?: any) => {
        if (err) {
          reject(err);
        } else {
          resolve();
        }

        this.onLoaded = null;
      };
    });
  }

  public render() {
    return (
      <Styles>
        {(css) => (
          <MediaElement<MediaType.Audio>
            type={MediaType.Audio}
            src={this.props.src}
            playback={this.playback}
            className={css('invisible')}
            preload="auto"
          />)}
      </Styles>
    );
  }

  /**
   * Blocks until playback is complete.
   * Does not propagate errors.
   * Will throw if not called concurrently.
   */
  public async play(): Promise<void> {
    // Currently playing
    if (this.onPlaybackFinished) {
      throw new Error('SimpleSoundPlayer: attempted concurrent playback');
    }

    try {
      await this.loadedPromise;

      // If loading failed, loadedPromise will have thrown.

      const playbackFinished = new Promise<void>((resolve, reject) => {
        this.onPlaybackFinished = () => {
          resolve();
          this.onPlaybackFinished = null;
        };
      });

      // Promise constructors are called immediately: this.onPlaybackFinished will be set.

      try {
        if (!this.outlet) {
          // Calls to play() after the outlet is removed are trivially completed.
          this.onPlaybackFinished!();
        } else {
          // most errors are reported separately, via `onError`.
          // but we should account for the possibility of `play()` throwing
          await this.outlet.play();
        }
      } catch (e) {
        // Any exception after the callback is setup must signal playback finished.
        // An event handler might have already signaled.
        if (this.onPlaybackFinished) {
          this.onPlaybackFinished!();
        }
        throw e;
      }

      return await playbackFinished;
    } catch (e) {
      notify(e, { name: 'SimpleMediaPlayer.play()' });
    }
  }
}

const Styles = createStyles({
  invisible: {
    display: 'none',
  },
});
